// @code-guard: skip-file reason="Legacy G2 chart module pending design-token migration and component breakup"
import React, { FC, memo, useContext, useEffect, useMemo, useRef } from 'react';
import { Spin } from 'antd';
import { renderToStaticMarkup } from 'react-dom/server';

import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import TrendArrowUp from 'assets/svg/TrendArrowUp';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { formatNumWithKMBAlt, getConfig, getMaxBound, getMinBound, scaleMaxValue, scaleMinValue } from 'utils/helperMethods';

import {
    computePpaBubbleSizing,
    PPA_CHART_COLUMN_WIDTH_PX,
    PPA_CHART_TOP_APPEND_PADDING_PX,
    PPA_CHART_X_AXIS_CHROME_HEIGHT_PX,
    PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO,
} from './packArchitectureTable.helpers';

if (typeof window !== 'undefined') {
    // @ts-ignore
    var G2 = require('@antv/g2');
    var { Chart } = G2;
}

interface Props {
    dataset: Array<any>;
    chartContainer: string;
    height: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    stroke?: string;
    loading?: boolean;
    xRange?: { xMin?: number; xMax?: number };
    yRange?: { xMin?: number; xMax?: number };
    xTickLabels?: string[];
    yTickValues?: number[];
    hideYAxisLabels?: boolean;
    hideYAxisTitle?: boolean;
    hideYAxisLine?: boolean;
    activeSegmentId?: string | null;
    segmentHighlightConfig?: {
        mode: 'heatMap' | 'segment';
        fillColor: string;
        fillOpacity: number;
        lineColor: string;
        useSegmentAvgLabel?: boolean;
        avgGrowthText?: string;
    };
    activeSegmentBreakpointsOverride?: ActiveSegmentBreakpoints | null;
    onBubbleClick?: (datum: Record<string, unknown>) => void;
    onBubbleHover?: (datum: Record<string, unknown>) => void;
    onBubbleHoverEnd?: () => void;
    onChartBackgroundClick?: () => void;
    onYAxisBreakpointLabelPositions?: (positions: YAxisBreakpointLabelPosition[]) => void;
    onYAxisTickLabelPositions?: (positions: YAxisTickLabelPosition[]) => void;
}

export type YAxisBreakpointLabelPosition = {
    label: string;
    topPx: number;
    yValue: number;
    color?: string;
};

export type YAxisTickLabelPosition = {
    yValue: number;
    topPx: number;
};

export type ActiveSegmentBreakpoints = {
    minY: number;
    maxY: number;
    avgY: number;
    segmentColor: string;
    regionFillColor: string;
    regionFillOpacity: number;
    useSegmentAvgLabel: boolean;
    avgGrowthText?: string;
};

type BubbleHoverTooltipProps = {
    vendor?: string;
    segment?: string;
    kpis?: Array<{
        id?: string;
        label?: string;
        value?: string;
        growthText?: string;
        trendDirection?: string;
    }>;
};

type GrowthMeta = {
    value: string;
    direction: 'up' | 'down' | 'neutral';
};

type HeatMapValueAverageLabelProps = {
    bubbleName?: string;
    showBubbleName?: boolean;
    valueLabel?: string;
    value?: string;
    metricLabel?: string;
    metricValue?: string;
    metricDirection?: 'up' | 'down' | 'neutral';
};

/** SSR/no-canvas fallback only - real truncation below measures actual pixel width. */
const X_AXIS_LABEL_CHAR_LIMIT_FALLBACK = Math.max(16, Math.floor(PPA_CHART_COLUMN_WIDTH_PX / 16));
const CHART_DECIMAL_PRECISION = 1;
const HOVER_BUBBLE_OUTLINE_WIDTH = 3;
const HOVER_BUBBLE_SHADOW_COLOR = 'rgba(0, 0, 0, 0.16)';
const HOVER_BUBBLE_SHADOW_BLUR = 12;
const HOVER_BUBBLE_SHADOW_OFFSET_Y = 2;
const HOVER_GUIDE_LINE_COLOR = '#c7c7cf';
const LARGE_DATASET_LABEL_THRESHOLD = 1200;
const LARGE_DATASET_INTERACTION_THRESHOLD = 1800;
const SVG_RENDERER_WIDTH_TRIGGER_PX = 32000;
const PINPOINT_DIMMED_FILL_OPACITY = 0.05;
const PINPOINT_DIMMED_STROKE_OPACITY = 0.45;
const PINPOINT_SELECTED_Z_INDEX = 10000;
const ACTIVE_SEGMENT_Z_INDEX_BASE = 5000;
const HOVER_CROSSHAIR_Z_INDEX = 15000;
const HOVERED_BUBBLE_Z_INDEX = 20000;
const BUBBLE_TOOLTIP_BOX_SHADOW =
    '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)';
const Y_AXIS_MIN_MAX_LABEL_COLOR = '#71717B';
// Min/Avg/Max text is ~10px with tight leading; anything closer than this on
// screen visually collides (letters overlapping), so segments whose min/avg/max
// values are close together need their label positions nudged apart.
const Y_AXIS_BREAKPOINT_LABEL_MIN_GAP_PX = 13;
const AXIS_LABEL_ELLIPSIS = '...';
/** Breathing room between adjacent column labels, on top of the fit-to-width truncation. */
const AXIS_LABEL_HORIZONTAL_GUTTER_PX = 6;
const X_AXIS_TICK_LABEL_FONT_SIZE_PX = 12;

let axisLabelMeasureContext: CanvasRenderingContext2D | null | undefined;
/** Lazily-created, reused offscreen canvas for measuring label pixel widths. */
const getAxisLabelMeasureContext = (): CanvasRenderingContext2D | null => {
    if (axisLabelMeasureContext !== undefined) return axisLabelMeasureContext;
    axisLabelMeasureContext = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    return axisLabelMeasureContext;
};

/**
 * Truncates `label` (appending "...") so its rendered width fits within
 * `maxWidthPx` for the given canvas `font` string. Column width is now
 * derived from bubble size (not a fixed 144px), so a fixed character-count
 * limit could either overflow a narrower column (labels overlapping, as
 * happens with long descriptions) or under-truncate a wider one - measuring
 * actual pixel width against the real per-column budget fixes both.
 */
const truncateAxisLabelToWidth = (label: string | undefined, maxWidthPx: number, font: string): string => {
    if (!label) return '';
    const ctx = getAxisLabelMeasureContext();
    if (!ctx || !Number.isFinite(maxWidthPx) || maxWidthPx <= 0) {
        return label.length > X_AXIS_LABEL_CHAR_LIMIT_FALLBACK
            ? `${label.slice(0, X_AXIS_LABEL_CHAR_LIMIT_FALLBACK)}${AXIS_LABEL_ELLIPSIS}`
            : label;
    }
    ctx.font = font;
    if (ctx.measureText(label).width <= maxWidthPx) return label;
    if (ctx.measureText(AXIS_LABEL_ELLIPSIS).width > maxWidthPx) return AXIS_LABEL_ELLIPSIS;

    let low = 0;
    let high = label.length;
    let bestFitLength = 0;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidateWidth = ctx.measureText(`${label.slice(0, mid)}${AXIS_LABEL_ELLIPSIS}`).width;
        if (candidateWidth <= maxWidthPx) {
            bestFitLength = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return `${label.slice(0, bestFitLength)}${AXIS_LABEL_ELLIPSIS}`;
};

const parseGrowthMeta = (growth?: string, trendDirection?: string): GrowthMeta | null => {
    const value = growth?.trim();
    if (!value || value === '-') return null;
    if (trendDirection === 'down') return { value: value.replace('-', '').replace('+', ''), direction: 'down' };
    if (trendDirection === 'up') return { value: value.replace('-', '').replace('+', ''), direction: 'up' };
    if (value.startsWith('-')) return { value: value.replace('-', ''), direction: 'down' };
    if (value.startsWith('+')) return { value: value.replace('+', ''), direction: 'up' };
    return { value, direction: 'neutral' };
};

const formatYAxisTickLabel = (value: number, decimals: number) => {
    if (!Number.isFinite(value)) return '-';
    if (Math.abs(value) >= 1) {
        return formatNumWithKMBAlt(value, decimals, decimals);
    }
    return value.toFixed(decimals);
};

const HEAT_MAP_LABEL_TEXT_STYLE: React.CSSProperties = {
    fontSize: '10px',
    lineHeight: '18px',
    fontStyle: 'normal',
    whiteSpace: 'nowrap',
};

const PackArchitectureHeatMapValueAverageLabel: FC<HeatMapValueAverageLabelProps> = ({
    bubbleName = '',
    showBubbleName = false,
    valueLabel = 'Value',
    value = '-',
    metricLabel = 'Avg.',
    metricValue = '-',
    metricDirection = 'neutral',
}) => {
    const growthColor = metricDirection === 'down' ? colorConstants.growthDown : metricDirection === 'up' ? colorConstants.growthUp : '#52525c';
    return (
        <div
            style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                pointerEvents: 'none',
            }}
        >
            {showBubbleName && bubbleName ? (
                <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#3F3F46', fontWeight: 400, lineHeight: '14px' }}>
                    {bubbleName}
                </span>
            ) : null}
            <div style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#71717b', fontWeight: 400 }}>{valueLabel}</span>
                    <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#52525c', fontWeight: 500 }}>{value}</span>
                </div>
                <div
                    style={{
                        alignSelf: 'stretch',
                        width: '1px',
                        minHeight: '20px',
                        backgroundColor: '#d9d9d9',
                    }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#71717b', fontWeight: 400 }}>{metricLabel}</span>
                    <span
                        style={{
                            ...HEAT_MAP_LABEL_TEXT_STYLE,
                            color: metricLabel === 'Growth' ? growthColor : '#52525c',
                            fontWeight: 500,
                        }}
                    >
                        {metricValue}
                    </span>
                </div>
            </div>
        </div>
    );
};

// @code-guard: skip-next-function reason="Legacy tooltip UI uses fixed visual palette; tracked for design-token migration"
const PackArchitectureBubbleHoverTooltip: FC<BubbleHoverTooltipProps> = ({
    vendor,
    segment,
    kpis = [],
}) => {
    const visibleKpis = kpis.filter((kpi) => Boolean(kpi.label) || Boolean(kpi.value));
    return (
        <div className="w-[250px] rounded-[12px] bg-white">
            <div className="flex gap-1 pb-1 w-full">
                <span className="max-w-1/2 break-words text-[12px] font-semibold leading-[18px] text-blackTitle">
                    {vendor}
                </span>
                <span className="shrink-0 px-1 text-[8px] leading-[18px] text-blackTitle">x</span>
                <span className="max-w-1/2 break-words text-[12px] font-semibold leading-[18px] text-blackTitle">
                    {segment}
                </span>
            </div>
            <div className="mb-3 mt-2 border-t border-[#E4E4E7]" />

            <div className="flex flex-col gap-2">
                {visibleKpis.map((kpi) => {
                    const growthMeta = parseGrowthMeta(kpi.growthText, kpi.trendDirection);
                    return (
                        <div key={kpi.id || kpi.label} className="flex items-center justify-between">
                            <span className="text-[12px] font-normal text-[#71717b]">{kpi.label || '-'}</span>
                            <div className="flex items-center gap-1">
                                <span className="text-[12px] font-normal text-[#18181b]">{kpi.value || '-'}</span>
                                {growthMeta ? (
                                    <span
                                        className="inline-flex items-center gap-0.5 text-[12px]"
                                        style={{
                                            color:
                                                growthMeta.direction === 'down'
                                                    ? colorConstants.growthDown
                                                    : colorConstants.growthUp,
                                        }}
                                    >
                                        {growthMeta.direction === 'down' ? (
                                            <TrendArrowDown className="shrink-0" color={colorConstants.growthDown} />
                                        ) : (
                                            <TrendArrowUp className="shrink-0" color={colorConstants.growthUp} />
                                        )}
                                        <span>{growthMeta.value}</span>
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
                {visibleKpis.length === 0 ? (
                    <div className="flex items-center justify-between">
                        <span className="text-[12px] font-normal text-[#71717b]">No KPI data</span>
                        <span className="text-[12px] font-normal text-[#18181b]">-</span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

type ActiveSelectionMode = 'segment' | 'column' | null;

const resolveActiveSelectionMode = (
    rows: Array<Record<string, unknown>>,
    activeSegmentId: string | null,
): ActiveSelectionMode => {
    if (!activeSegmentId) return null;

    let matchesColumn = false;
    for (const row of rows) {
        const segmentKey = row.segmentKey;
        if (typeof segmentKey === 'string' && segmentKey && segmentKey === activeSegmentId) return 'segment';
        const columnKey = row.columnKey;
        if (typeof columnKey === 'string' && columnKey && columnKey === activeSegmentId) {
            matchesColumn = true;
        }
    }

    return matchesColumn ? 'column' : null;
};

const isActiveSegmentRow = (
    row: Record<string, unknown>,
    activeSegmentId: string,
    activeSelectionMode: Exclude<ActiveSelectionMode, null>,
) => {
    const segmentKey = row.segmentKey;
    if (activeSelectionMode === 'segment') {
        return typeof segmentKey === 'string' && segmentKey && segmentKey === activeSegmentId;
    }
    const columnKey = row.columnKey;
    return typeof columnKey === 'string' && columnKey && columnKey === activeSegmentId;
};

const BUBBLE_SIZE_FIELD = 'bubbleSizeValue';

/**
 * Spread out breakpoint labels (min/avg/max) that sit within `minGapPx` of
 * each other on screen, so their text doesn't overlap when a segment's
 * values are close together (e.g. low-variance segments). Positions are
 * nudged symmetrically around their shared centroid rather than simply
 * cascaded downward, so a tight cluster ends up centered on where it
 * originally sat instead of drifting toward one edge of the plot.
 */
const resolveYAxisBreakpointLabelCollisions = <T extends { topPx: number }>(
    positions: T[],
    minGapPx: number,
): T[] => {
    if (positions.length < 2) return positions;

    const sorted = positions
        .map((position) => ({ ...position }))
        .sort((left, right) => left.topPx - right.topPx);
    // A handful of passes is enough to converge even with several labels: each
    // pass only pushes immediate neighbours apart, so a tight cluster needs a
    // few iterations to fully spread out to `minGapPx` between every pair.
    const maxPasses = Math.max(10, sorted.length * 4);
    for (let pass = 0; pass < maxPasses; pass += 1) {
        let resolvedAll = true;
        for (let index = 1; index < sorted.length; index += 1) {
            const gap = sorted[index].topPx - sorted[index - 1].topPx;
            if (gap >= minGapPx) continue;
            const shortfall = minGapPx - gap;
            sorted[index - 1].topPx -= shortfall / 2;
            sorted[index].topPx += shortfall / 2;
            resolvedAll = false;
        }
        if (resolvedAll) break;
    }
    return sorted;
};

/** Largest-first draw order keeps smaller bubbles layered above larger overlaps. */
const sortBubblesLargestFirst = <T extends Record<string, unknown>>(rows: T[]) =>
    [...rows].sort((left, right) => {
        const leftSize = Number(left[BUBBLE_SIZE_FIELD]) || 0;
        const rightSize = Number(right[BUBBLE_SIZE_FIELD]) || 0;
        return rightSize - leftSize;
    });

// @code-guard: skip-next-function reason="Large legacy G2 chart setup pending split into hooks and tokenized constants"
const BubbleScatterChart: FC<Props> = ({
    dataset,
    chartContainer,
    height,
    xAxisTitle,
    yAxisTitle,
    stroke,
    loading,
    xRange = {},
    yRange = {},
    xTickLabels,
    yTickValues,
    hideYAxisLabels = false,
    hideYAxisTitle = false,
    hideYAxisLine = false,
    activeSegmentId = null,
    segmentHighlightConfig,
    activeSegmentBreakpointsOverride = null,
    onBubbleClick,
    onBubbleHover,
    onBubbleHoverEnd,
    onChartBackgroundClick,
    onYAxisBreakpointLabelPositions,
    onYAxisTickLabelPositions,
}) => {
    const X_FIELD = 'xFieldValue';
    const Y_FIELD = 'yFieldValue';
    const COLOR_FIELD = 'bubbleColor';
    const SIZE_FIELD = 'bubbleSizeValue';

    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontFamily } = commonSetting;
    const activeSelectionMode = useMemo(
        () => resolveActiveSelectionMode(dataset, activeSegmentId),
        [activeSegmentId, dataset],
    );
    const chartData = useMemo(() => {
        const sortWithPinpointOnTop = (rows: Array<any>) => {
            const hasPinpointFocus = rows.some((row) => row.isPinpointSelected);
            if (!hasPinpointFocus) return rows;

            const selectedRows = rows.filter((row) => row.isPinpointSelected);
            const otherRows = rows.filter((row) => !row.isPinpointSelected);
            return [...sortBubblesLargestFirst(otherRows), ...sortBubblesLargestFirst(selectedRows)];
        };

        if (!activeSegmentId || !activeSelectionMode) {
            return sortWithPinpointOnTop(sortBubblesLargestFirst(dataset));
        }
        const inactiveRows: Array<any> = [];
        const activeRows: Array<any> = [];
        dataset.forEach((row) => {
            if (isActiveSegmentRow(row, activeSegmentId, activeSelectionMode)) {
                activeRows.push(row);
            } else {
                inactiveRows.push(row);
            }
        });
        return sortWithPinpointOnTop([
            ...sortBubblesLargestFirst(inactiveRows),
            ...sortBubblesLargestFirst(activeRows),
        ]);
    }, [activeSegmentId, activeSelectionMode, dataset]);
    const activeSegmentBreakpoints = useMemo<ActiveSegmentBreakpoints | null>(() => {
        if (activeSegmentBreakpointsOverride) return activeSegmentBreakpointsOverride;
        if (!activeSegmentId || !activeSelectionMode) return null;
        let count = 0;
        let sumY = 0;
        let minY = Number.POSITIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        let segmentBaseColor = '#7A3FF2';

        chartData.forEach((row) => {
            if (!isActiveSegmentRow(row, activeSegmentId, activeSelectionMode)) return;
            const yValue = Number(row[Y_FIELD]);
            if (!Number.isFinite(yValue)) return;
            if (count === 0) {
                segmentBaseColor = String(row?.segmentBaseColor ?? row?.[COLOR_FIELD] ?? '#7A3FF2');
            }
            count += 1;
            sumY += yValue;
            minY = Math.min(minY, yValue);
            maxY = Math.max(maxY, yValue);
        });

        if (count === 0) return null;
        const avgY = sumY / count;

        if (segmentHighlightConfig?.mode === 'heatMap') {
            return {
                minY,
                maxY,
                avgY,
                segmentColor: segmentHighlightConfig.lineColor,
                regionFillColor: segmentHighlightConfig.fillColor,
                regionFillOpacity: segmentHighlightConfig.fillOpacity,
                useSegmentAvgLabel: Boolean(segmentHighlightConfig.useSegmentAvgLabel),
                avgGrowthText: segmentHighlightConfig.avgGrowthText,
            };
        }

        return {
            minY,
            maxY,
            avgY,
            segmentColor: segmentBaseColor,
            regionFillColor: segmentBaseColor,
            regionFillOpacity: 0.13,
            useSegmentAvgLabel: false,
            avgGrowthText: undefined,
        };
    }, [activeSegmentBreakpointsOverride, activeSegmentId, activeSelectionMode, chartData, segmentHighlightConfig]);
    const isLargeDatasetForLabels = chartData.length > LARGE_DATASET_LABEL_THRESHOLD;
    const isLargeDatasetForInteractions = chartData.length > LARGE_DATASET_INTERACTION_THRESHOLD;
    const shouldRenderGrowthPctLabels = useMemo(
        () => !isLargeDatasetForLabels && chartData.some((row) => Boolean(row?.showGrowthPctLabel)),
        [chartData, isLargeDatasetForLabels],
    );
    const shouldRenderInfoLabels = useMemo(
        () => !isLargeDatasetForLabels && chartData.some((row) => Boolean(row?.showBubbleInfoLabel)),
        [chartData, isLargeDatasetForLabels],
    );
    const hasCustomYTicks = Array.isArray(yTickValues) && yTickValues.length > 1;
    const estimatedScrollableWidthPx = useMemo(() => {
        const estimatedColumns = Math.max(1, xTickLabels?.length ?? chartData.length ?? 0);
        return estimatedColumns * PPA_CHART_COLUMN_WIDTH_PX;
    }, [chartData.length, xTickLabels]);
    const shouldUseSvgRenderer = estimatedScrollableWidthPx > SVG_RENDERER_WIDTH_TRIGGER_PX;
    const chartRef = useRef<any>(null);
    const applyBubbleZOrderRef = useRef<(() => void) | null>(null);
    const onBubbleClickRef = useRef<Props['onBubbleClick']>(onBubbleClick);
    const onBubbleHoverRef = useRef<Props['onBubbleHover']>(onBubbleHover);
    const onBubbleHoverEndRef = useRef<Props['onBubbleHoverEnd']>(onBubbleHoverEnd);
    const onChartBackgroundClickRef = useRef<Props['onChartBackgroundClick']>(onChartBackgroundClick);
    const onYAxisBreakpointLabelPositionsRef = useRef<Props['onYAxisBreakpointLabelPositions']>(
        onYAxisBreakpointLabelPositions,
    );
    const onYAxisTickLabelPositionsRef = useRef<Props['onYAxisTickLabelPositions']>(onYAxisTickLabelPositions);

    useEffect(() => {
        onBubbleClickRef.current = onBubbleClick;
        onBubbleHoverRef.current = onBubbleHover;
        onBubbleHoverEndRef.current = onBubbleHoverEnd;
        onChartBackgroundClickRef.current = onChartBackgroundClick;
        onYAxisBreakpointLabelPositionsRef.current = onYAxisBreakpointLabelPositions;
        onYAxisTickLabelPositionsRef.current = onYAxisTickLabelPositions;
    }, [
        onBubbleClick,
        onBubbleHover,
        onBubbleHoverEnd,
        onChartBackgroundClick,
        onYAxisBreakpointLabelPositions,
        onYAxisTickLabelPositions,
    ]);

    useEffect(() => {
        if (!window) return;

        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }

        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: false,
            follow: true,
        };

        const chart = new Chart({
            container: chartContainer,
            autoFit: true,
            height,
            appendPadding: [PPA_CHART_TOP_APPEND_PADDING_PX, 10, 10, 0],
            renderer: shouldUseSvgRenderer ? 'svg' : 'canvas',
        });

        chart.data(chartData);

        const minX: any = getMinBound(chartData, [X_FIELD]);
        const minY: any = getMinBound(chartData, [Y_FIELD]);
        const maxX: any = getMaxBound(chartData, [X_FIELD]);
        const maxY: any = getMaxBound(chartData, [Y_FIELD]);
        const xTickCount = Math.max(1, xTickLabels?.length ?? 0);
        const hasExplicitXAxisCategories = Boolean(xTickLabels && xTickLabels.length > 0);
        // Align chart column centers with table column centers: each column gets
        // equal pitch and half-column inset at both edges when categories exist.
        const categoryInset = xTickCount > 1 ? 1 / (2 * xTickCount) : 0;
        const xScaleRangeStart = hasExplicitXAxisCategories
            ? xTickCount === 1
                ? 0.5
                : categoryInset
            : Math.min(0.05, 0.5 / xTickCount);
        const xScaleRangeEnd = hasExplicitXAxisCategories
            ? xTickCount === 1
                ? 0.5
                : 1 - categoryInset
            : 1 - xScaleRangeStart;
        // Single source of truth for bubble radius, shared with ReusableBubbleChart
        // (which uses the same formula, fed the same `height`, to size the chart
        // canvas/columns). Y-axis headroom *above* the largest bubble is handled by
        // ReusableBubbleChart expanding the Y domain max by
        // PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO x maxDiameterPx before it ever
        // reaches this component. The domain *min*, however, is now pinned to 0
        // (chart starts at the origin) rather than padded negative, so bubble
        // clearance at the bottom can't come from the domain - it's reserved
        // below as a fixed pixel inset on this scale's `range` instead, sized to
        // the largest bubble any row could ever render at, so nothing can
        // overlap the x-axis regardless of how close its value sits to 0.
        const {
            minRadiusPx: minBubbleRadiusPx,
            maxRadiusPx: maxBubbleRadiusPx,
            maxDiameterPx,
            columnWidthPx,
            columnGapPx,
        } = computePpaBubbleSizing(height);
        // Per-column pitch (bubble's own column band + the gap to the next one),
        // minus a little breathing room, is the real budget a column's X-axis
        // label has before it starts overlapping its neighbour.
        const xAxisLabelMaxWidthPx = Math.max(0, columnWidthPx + columnGapPx - AXIS_LABEL_HORIZONTAL_GUTTER_PX);
        const xAxisLabelFont = `${X_AXIS_TICK_LABEL_FONT_SIZE_PX}px ${commonSetting.graphFontFamily}`;
        const resolvedYScaleMin =
            typeof yRange?.xMin === 'number' ? yRange.xMin : chartData?.length === 1 ? scaleMinValue(10, minY) : null;
        const resolvedYScaleMax =
            typeof yRange?.xMax === 'number' ? yRange.xMax : chartData?.length === 1 ? scaleMaxValue(10, maxY) : null;
        // Same chrome accounting as ReusableBubbleChart's Y-axis padding math
        // (this component's own `appendPadding` top + the visible X-axis's
        // auto-reserved line/label/title chrome), so the fraction below maps
        // to the intended pixel count on the *actual* plot area, not the
        // whole container.
        const estimatedPlotHeightPx = Math.max(
            1,
            height - PPA_CHART_TOP_APPEND_PADDING_PX - PPA_CHART_X_AXIS_CHROME_HEIGHT_PX,
        );
        // Spec parity with top padding: reserve 60% of the largest rendered
        // bubble diameter at the bottom too, so a bubble centered at/near 0
        // never visually drops below the X-axis baseline.
        const yAxisBottomInsetPx = maxDiameterPx * PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO;
        const yScaleRangeStart = Math.min(0.4, yAxisBottomInsetPx / estimatedPlotHeightPx);

        chart.scale({
            [X_FIELD]: {
                nice: !xTickLabels || xTickLabels.length === 0,
                min: typeof xRange?.xMin === 'number' ? xRange.xMin : chartData?.length === 1 ? scaleMinValue(10, minX) : null,
                max: typeof xRange?.xMax === 'number' ? xRange.xMax : chartData?.length === 1 ? scaleMaxValue(10, maxX) : null,
                ...(xTickLabels && xTickLabels.length > 0
                    ? {
                          tickInterval: 1,
                      }
                    : {}),
                range: [xScaleRangeStart, xScaleRangeEnd],
            },
            [Y_FIELD]: {
                nice: !hasCustomYTicks,
                min: resolvedYScaleMin,
                max: resolvedYScaleMax,
                ...(hasCustomYTicks ? { ticks: yTickValues } : {}),
                // Reserve a fixed pixel band at the very bottom of the plot for
                // the largest possible bubble's radius + gutter, so a bubble
                // sitting right at the (now-pinned) domain min of 0 still
                // renders fully above the x-axis line instead of overlapping it.
                range: [yScaleRangeStart, 1],
            },
            // Fixed [0, 1] domain: the backend already normalises bubbleSizeValue
            // globally (across all pages), so pinning the domain here keeps a
            // bubble's pixel size stable regardless of which page/zoom window is
            // currently loaded, instead of G2 re-deriving min/max from whatever
            // subset of rows happens to be in `chartData` right now.
            [SIZE_FIELD]: {
                type: 'linear',
                min: 0,
                max: 1,
            },
        });

        chart.tooltip({
            ...defaultTooltipConfig,
            shared: false,
            crosshairs: false,
            containerTpl:
                `<div class="g2-tooltip" style="min-width:200px; border-radius:12px; background:#ffffff; box-shadow:${BUBBLE_TOOLTIP_BOX_SHADOW};">` +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list"></ul></div>',
            domStyles: {
                'g2-tooltip': {
                    borderRadius: '12px',
                    background: '#ffffff',
                    boxShadow: BUBBLE_TOOLTIP_BOX_SHADOW,
                },
                'g2-tooltip-list': {
                    margin: '0',
                    padding: '0',
                },
            },
            customContent: (_title: string, items: any[] = []) => {
                const datum = items?.[0]?.data || {};
                return renderToStaticMarkup(
                    <PackArchitectureBubbleHoverTooltip
                        vendor={datum.vendor}
                        segment={datum.segment}
                        kpis={datum.tooltipKpis}
                    />,
                );
            },
        });

        chart.axis(X_FIELD, {
            title: {
                text: xAxisTitle,
                style: {
                    fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '400',
                },
            },
            label: {
                formatter: (val: any) => {
                    if (xTickLabels && xTickLabels.length > 0) {
                        const roundedIndex = Math.round(Number(val));
                        if (!Number.isNaN(roundedIndex) && roundedIndex >= 1 && roundedIndex <= xTickLabels.length) {
                            return truncateAxisLabelToWidth(xTickLabels[roundedIndex - 1], xAxisLabelMaxWidthPx, xAxisLabelFont);
                        }
                    }
                    return formatNumWithKMBAlt(val, CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION);
                },
                autoRotate: false,
                autoHide: false,
                style: {
                    fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: stroke,
                        lineWidth: 0,
                        lineDash: [0, 2],
                    },
                },
            },
        });

        chart.axis(Y_FIELD, {
            title: hideYAxisTitle
                ? false
                : {
                      text: yAxisTitle,
                      style: {
                          fontSize: graphFontSize,
                          fontFamily: commonSetting.graphFontFamily,
                          fill: graphLabelColor,
                          fontWeight: '500',
                      },
                  },
            label: hideYAxisLabels
                ? false
                : {
                      formatter: (val: any) => {
                          return formatYAxisTickLabel(Number(val), CHART_DECIMAL_PRECISION);
                      },
                      style: {
                          fontSize: graphFontSize,
                          fontFamily: commonSetting.graphFontFamily,
                          fill: graphLabelColor,
                      },
                  },
            line: hideYAxisLine
                ? false
                : {
                      style: {
                          stroke: '#D9D9D9',
                          lineWidth: 1,
                      },
                  },
            grid: {
                line: {
                    style: {
                        stroke: stroke,
                        lineWidth: 1,
                        lineDash: [1, 2],
                    },
                },
            },
        });

        const pointGeometry = chart
            .point()
            .position(`${X_FIELD}*${Y_FIELD}`)
            .size(SIZE_FIELD, [minBubbleRadiusPx, maxBubbleRadiusPx])
            .color(COLOR_FIELD, (colorValue: string) => colorValue);

        pointGeometry.sortZIndex = false;

        pointGeometry
            .shape('circle')
            .style({
                fields: ['highlightLabel', 'isPinpointSelected', 'isPinpointDimmed'],
                callback: (highlightLabel: string, isPinpointSelected: boolean, isPinpointDimmed: boolean) => {
                    const isSegmentActive = Boolean(activeSegmentId);
                    let fillOpacity = 0.85;
                    let strokeOpacity = 1;

                    if (isPinpointSelected) {
                        fillOpacity = 1;
                        strokeOpacity = 1;
                    } else if (isPinpointDimmed) {
                        fillOpacity = PINPOINT_DIMMED_FILL_OPACITY;
                        strokeOpacity = PINPOINT_DIMMED_STROKE_OPACITY;
                    } else if (isSegmentActive) {
                        fillOpacity = highlightLabel ? 1 : PINPOINT_DIMMED_FILL_OPACITY;
                        strokeOpacity = highlightLabel ? 1 : PINPOINT_DIMMED_STROKE_OPACITY;
                    }

                    return {
                        // A clicked/selected bubble (cell click) should carry the exact
                        // same outline as the hover `active` state below, so it reads as
                        // "selected" rather than just "opaque like its highlighted segment".
                        lineWidth: isPinpointSelected ? HOVER_BUBBLE_OUTLINE_WIDTH : 0,
                        stroke: '#FFFFFF',
                        strokeOpacity,
                        fillOpacity,
                        shadowColor: isPinpointSelected ? HOVER_BUBBLE_SHADOW_COLOR : 'transparent',
                        shadowBlur: isPinpointSelected ? HOVER_BUBBLE_SHADOW_BLUR : 0,
                        shadowOffsetY: isPinpointSelected ? HOVER_BUBBLE_SHADOW_OFFSET_Y : 0,
                        cursor: 'pointer',
                    };
                },
            })
            .state({
                active: {
                    style: {
                        lineWidth: HOVER_BUBBLE_OUTLINE_WIDTH,
                        stroke: '#FFFFFF',
                        strokeOpacity: 1,
                        fillOpacity: 1,
                        shadowColor: HOVER_BUBBLE_SHADOW_COLOR,
                        shadowBlur: HOVER_BUBBLE_SHADOW_BLUR,
                        shadowOffsetY: HOVER_BUBBLE_SHADOW_OFFSET_Y,
                        cursor: 'pointer',
                    },
                },
            });

        // Shared size-range computation used by both label branches to derive
        // per-bubble radius → dynamic offsetX (bubble edge + 4 px gap).
        const sizeValues = chartData.map((d: any) => Number(d[SIZE_FIELD]) || 0);
        const minSizeValue = Math.min(...sizeValues);
        const maxSizeValue = Math.max(...sizeValues);
        const sizeRange = maxSizeValue - minSizeValue;

        const getBubbleOffsetX = (rawSize: number) => {
            const t = sizeRange > 0 ? (rawSize - minSizeValue) / sizeRange : 0;
            // G2 interprets .size() as radius, so interpolate over the same
            // [minBubbleRadiusPx, maxBubbleRadiusPx] bounds fed to .size() above.
            const bubbleRadiusPx = minBubbleRadiusPx + t * (maxBubbleRadiusPx - minBubbleRadiusPx);
            return Math.round(bubbleRadiusPx) + 4;
        };

        if (shouldRenderGrowthPctLabels) {
            // Canvas-rendered labels (cheap) for the heat-map "growth % per bubble"
            // mode. This can apply to every bubble across all loaded pages, so we
            // must avoid per-bubble HTML annotations here to prevent UI hangs.
            pointGeometry.label(
                `growthText*infoLabelMetricDirection*showGrowthPctLabel*${SIZE_FIELD}`,
                (growthText: string, direction: string, showLabel: boolean, rawSize: number) => {
                    if (!showLabel || !growthText || growthText === '-') return null;
                    const fill =
                        direction === 'down'
                            ? colorConstants.growthDown
                            : direction === 'up'
                              ? colorConstants.growthUp
                              : colorConstants.greyAlt15;
                    return {
                        offset: 0,
                        offsetX: getBubbleOffsetX(rawSize),
                        offsetY: 8,
                        content: growthText,
                        style: {
                            fontSize: 10,
                            lineHeight: 18,
                            fontWeight: 500,
                            fill,
                            fontFamily: graphFontFamily,
                            textAlign: 'left',
                            textBaseline: 'middle',
                        },
                    };
                },
            );

            const bubbleNameLabelGeometry = chart.point().position(`${X_FIELD}*${Y_FIELD}`).size(0);
            bubbleNameLabelGeometry.style({
                lineWidth: 0,
                fillOpacity: 0,
                strokeOpacity: 0,
            });
            bubbleNameLabelGeometry.label(
                `segment*showGrowthPctLabel*${SIZE_FIELD}`,
                (bubbleName: string, showLabel: boolean, rawSize: number) => {
                    if (!showLabel || !bubbleName) return null;
                    return {
                        offset: 0,
                        offsetX: getBubbleOffsetX(rawSize),
                        offsetY: -8,
                        content: bubbleName,
                        style: {
                            fontSize: 10,
                            lineHeight: 14,
                            fontWeight: 500,
                            fill: '#3F3F46',
                            fontFamily: graphFontFamily,
                            textAlign: 'left',
                            textBaseline: 'middle',
                        },
                    };
                },
            );
        } else if (shouldRenderInfoLabels) {
            // HTML annotations only for the active segment's bubbles (bounded count).
            chartData.forEach((datum: any) => {
                if (!datum.showBubbleInfoLabel) return;
                const dynamicOffsetX = getBubbleOffsetX(Number(datum[SIZE_FIELD]) || 0);
                chart.annotation().html({
                    position: [datum[X_FIELD], datum[Y_FIELD]],
                    html: renderToStaticMarkup(
                        <PackArchitectureHeatMapValueAverageLabel
                            bubbleName={
                                typeof datum.segment === 'string'
                                    ? datum.segment
                                    : typeof datum.name === 'string'
                                      ? datum.name
                                      : ''
                            }
                            showBubbleName={segmentHighlightConfig?.mode === 'heatMap'}
                            valueLabel={datum.infoLabelValueLabel}
                            value={datum.infoLabelValueText ?? datum.valueText}
                            metricLabel={datum.infoLabelMetric}
                            metricValue={datum.infoLabelMetricValue}
                            metricDirection={datum.infoLabelMetricDirection}
                        />,
                    ),
                    offsetX: dynamicOffsetX,
                    offsetY: -18,
                });
            });
        } else {
            pointGeometry.label('bubbleLabel', (label: string) => {
                if (!label) return null;
                const isNegative = String(label).trim().startsWith('-');
                return {
                    offsetX: 10,
                    offsetY: 0,
                    content: label,
                    style: {
                        fontSize: 12,
                        lineHeight: 14,
                        fontWeight: 600,
                        fill: isNegative ? '#ec343e' : '#159957',
                        fontFamily: graphFontFamily,
                        textAlign: 'left',
                    },
                };
            });
        }

        pointGeometry.tooltip('vendor*segment*tooltipKpis', () => ({}));

        type BubbleElement = {
            getData?: () => Record<string, unknown>;
            shape?: { set: (key: string, value: number) => void };
        };

        const applyBubbleZOrder = (hoveredElement?: BubbleElement) => {
            const elements = (pointGeometry.elements ?? []) as BubbleElement[];
            if (elements.length === 0) return;

            const hasSegmentFocus = Boolean(activeSegmentId);
            const hasPinpointFocus = elements.some((element) => Boolean(element.getData?.()?.isPinpointSelected));
            const maxNonHoveredZIndex = HOVER_CROSSHAIR_Z_INDEX - 1;

            const assignZIndex = (element: BubbleElement, zIndex: number) => {
                if (hoveredElement && element === hoveredElement) {
                    element.shape?.set('zIndex', HOVERED_BUBBLE_Z_INDEX);
                    return;
                }

                element.shape?.set('zIndex', Math.min(zIndex, maxNonHoveredZIndex));
            };

            if (hasSegmentFocus || hasPinpointFocus) {
                const rankedElements = elements
                    .map((element) => ({
                        element,
                        data: element.getData?.() ?? {},
                    }))
                    .sort((left, right) => {
                        const leftPinpoint = Boolean(left.data.isPinpointSelected) && hasPinpointFocus;
                        const rightPinpoint = Boolean(right.data.isPinpointSelected) && hasPinpointFocus;
                        if (leftPinpoint !== rightPinpoint) return leftPinpoint ? 1 : -1;

                        const leftActive = Boolean(left.data.highlightLabel);
                        const rightActive = Boolean(right.data.highlightLabel);
                        if (leftActive !== rightActive) return leftActive ? 1 : -1;

                        const leftSize = Number(left.data[SIZE_FIELD]) || 0;
                        const rightSize = Number(right.data[SIZE_FIELD]) || 0;
                        return rightSize - leftSize;
                    });

                let inactiveZ = 0;
                let activeZ = ACTIVE_SEGMENT_Z_INDEX_BASE;

                rankedElements.forEach(({ element, data }) => {
                    if (hasPinpointFocus && data.isPinpointSelected) {
                        assignZIndex(element, PINPOINT_SELECTED_Z_INDEX);
                        return;
                    }

                    if (hasSegmentFocus && data.highlightLabel) {
                        assignZIndex(element, activeZ);
                        activeZ += 1;
                        return;
                    }

                    assignZIndex(element, inactiveZ);
                    inactiveZ += 1;
                });
            } else {
                const rankedElements = elements
                    .map((element) => ({
                        element,
                        size: Number(element.getData?.()?.[SIZE_FIELD]) || 0,
                    }))
                    .sort((left, right) => right.size - left.size);

                rankedElements.forEach(({ element }, zIndex) => {
                    assignZIndex(element, zIndex);
                });
            }

            pointGeometry.container?.sort?.();
        };

        type HoverCrosshairGroup = {
            clear: () => void;
            show: () => void;
            hide: () => void;
            addShape: (type: string, cfg: Record<string, unknown>) => unknown;
            set: (key: string, value: number) => void;
        };

        let hoverCrosshairGroup: HoverCrosshairGroup | null = null;

        const updateHoverCrosshairs = (datum: Record<string, unknown> | null) => {
            if (!hoverCrosshairGroup) {
                hoverCrosshairGroup = pointGeometry.container?.addGroup({
                    zIndex: HOVER_CROSSHAIR_Z_INDEX,
                    name: 'bubble-hover-crosshair',
                    capture: false,
                }) as HoverCrosshairGroup | null;
            }

            if (!hoverCrosshairGroup) return;

            hoverCrosshairGroup.clear();

            if (!datum) {
                hoverCrosshairGroup.hide();
                return;
            }

            const point = chart.getXY({
                [X_FIELD]: datum[X_FIELD],
                [Y_FIELD]: datum[Y_FIELD],
            });
            if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
                hoverCrosshairGroup.hide();
                return;
            }

            const coordinate = chart.getCoordinate();
            hoverCrosshairGroup.show();
            hoverCrosshairGroup.set('zIndex', HOVER_CROSSHAIR_Z_INDEX);
            hoverCrosshairGroup.addShape('line', {
                attrs: {
                    x1: coordinate.start.x,
                    y1: point.y,
                    x2: coordinate.end.x,
                    y2: point.y,
                    stroke: HOVER_GUIDE_LINE_COLOR,
                    lineWidth: 1,
                },
            });
            hoverCrosshairGroup.addShape('line', {
                attrs: {
                    x1: point.x,
                    y1: coordinate.end.y,
                    x2: point.x,
                    y2: coordinate.start.y,
                    stroke: HOVER_GUIDE_LINE_COLOR,
                    lineWidth: 1,
                },
            });
            pointGeometry.container?.sort?.();
        };

        if (!isLargeDatasetForInteractions) {
            chart.interaction('element-active');
            chart.on('element:mouseenter', (event: { data?: { data?: Record<string, unknown> }; target?: BubbleElement }) => {
                const datum = event?.data?.data ?? null;
                updateHoverCrosshairs(datum);
                applyBubbleZOrder(event?.target);
                if (datum && onBubbleHoverRef.current) onBubbleHoverRef.current(datum);
            });
            chart.on('element:mouseleave', () => {
                updateHoverCrosshairs(null);
                applyBubbleZOrder();
                onBubbleHoverEndRef.current?.();
            });
        }

        let breakpointLabelReport: { xStart: number; labels: Array<{ label: string; y: number; color?: string }> } | null = null;

        if (activeSegmentBreakpoints) {
            // Extend annotation coordinates to the plot bounds while preserving
            // bubble and X-axis placements (which rely on X scale range insets).
            const xDomainMin = typeof xRange?.xMin === 'number' ? xRange.xMin : minX;
            const xDomainMax = typeof xRange?.xMax === 'number' ? xRange.xMax : maxX;
            const xDomainSpan = Math.max(1, xDomainMax - xDomainMin);
            const xRangeSpan = Math.max(0.01, xScaleRangeEnd - xScaleRangeStart);
            const leftInsetRatio = xScaleRangeStart / xRangeSpan;
            const rightInsetRatio = (1 - xScaleRangeEnd) / xRangeSpan;
            const xStart = xDomainMin - xDomainSpan * leftInsetRatio;
            const xEnd = xDomainMax + xDomainSpan * rightInsetRatio;
            chart.annotation().region({
                start: [xStart, activeSegmentBreakpoints.maxY],
                end: [xEnd, activeSegmentBreakpoints.minY],
                style: {
                    fill: activeSegmentBreakpoints.regionFillColor,
                    fillOpacity: activeSegmentBreakpoints.regionFillOpacity,
                },
                top: false,
            });
            const breakpointLabelCandidates: Array<{ label: string; y: number; color?: string }> = [];
            // Min/Avg/Max are always rendered as three distinct labels, even when a
            // segment has little/no spread (e.g. a single bubble, so min === avg ===
            // max) - collapsing them into one label when they coincide would make a
            // single-bubble segment silently lose its Min/Max markers. Any resulting
            // visual overlap is resolved afterwards in pixel space (see
            // resolveYAxisBreakpointLabelCollisions), not by dropping labels here.
            const addBreakpointLabel = (yValue: number, label?: string, showGuideLine = false, color?: string) => {
                breakpointLabelCandidates.push({
                    label: label ?? formatYAxisTickLabel(yValue, CHART_DECIMAL_PRECISION),
                    y: yValue,
                    color,
                });
                if (!showGuideLine) return;
                chart.annotation().line({
                    start: [xStart, yValue],
                    end: [xEnd, yValue],
                    style: {
                        stroke: activeSegmentBreakpoints.segmentColor,
                        lineDash: [4, 4],
                        lineWidth: 1,
                        opacity: 0.95,
                    },
                });
            };

            addBreakpointLabel(activeSegmentBreakpoints.maxY, undefined, false, Y_AXIS_MIN_MAX_LABEL_COLOR);
            addBreakpointLabel(
                activeSegmentBreakpoints.avgY,
                `Avg. ${formatYAxisTickLabel(activeSegmentBreakpoints.avgY, CHART_DECIMAL_PRECISION)}`,
                true,
            );
            addBreakpointLabel(activeSegmentBreakpoints.minY, undefined, false, Y_AXIS_MIN_MAX_LABEL_COLOR);
            breakpointLabelReport = { xStart, labels: breakpointLabelCandidates };
        }

        chart.legend(false);
        chart.axis(COLOR_FIELD, false);
        chart.axis(SIZE_FIELD, false);

        // Shared flag so plot:click can tell whether an element was just clicked,
        // preventing it from calling onChartBackgroundClick for bubble clicks.
        let elementClickOccurred = false;

        chart.on('element:click', (event: { data?: { data?: Record<string, unknown> } }) => {
            elementClickOccurred = true;
            queueMicrotask(() => {
                elementClickOccurred = false;
            });
            const datum = event?.data?.data;
            if (datum && onBubbleClickRef.current) onBubbleClickRef.current(datum);
        });

        chart.on('plot:click', () => {
            if (elementClickOccurred) return;
            onChartBackgroundClickRef.current?.();
        });

        try {
            chart.render();
            applyBubbleZOrder();
            chartRef.current = chart;
            applyBubbleZOrderRef.current = () => applyBubbleZOrder();
            const resolvedTickValues = Array.isArray(yTickValues) ? yTickValues : [];
            if (onYAxisTickLabelPositionsRef.current) {
                const xAxisAnchor =
                    typeof xRange?.xMin === 'number' ? xRange.xMin : Number.isFinite(minX) ? Number(minX) : 0;
                const tickPositions = resolvedTickValues
                    .map((yValue) => {
                        const point = chart.getXY({
                            [X_FIELD]: xAxisAnchor,
                            [Y_FIELD]: yValue,
                        });
                        return {
                            yValue,
                            topPx: typeof point?.y === 'number' ? point.y : Number.NaN,
                        };
                    })
                    .filter((position) => Number.isFinite(position.topPx));
                onYAxisTickLabelPositionsRef.current(tickPositions);
            }

            if (breakpointLabelReport && onYAxisBreakpointLabelPositionsRef.current) {
                const { xStart, labels } = breakpointLabelReport;
                const positions = labels
                    .map(({ label, y, color }) => {
                        const point = chart.getXY({
                            [X_FIELD]: xStart,
                            [Y_FIELD]: y,
                        });
                        return {
                            label,
                            topPx: typeof point?.y === 'number' ? point.y : 0,
                            yValue: y,
                            color,
                        };
                    })
                    .filter((position) => Number.isFinite(position.topPx));
                onYAxisBreakpointLabelPositionsRef.current(
                    resolveYAxisBreakpointLabelCollisions(positions, Y_AXIS_BREAKPOINT_LABEL_MIN_GAP_PX),
                );
            } else {
                onYAxisBreakpointLabelPositionsRef.current?.([]);
            }
        } catch (e) {
            console.error('Bubble chart render error', e);
            onYAxisBreakpointLabelPositionsRef.current?.([]);
            onYAxisTickLabelPositionsRef.current?.([]);
        }

        return () => {
            if (chartRef.current === chart) chartRef.current = null;
            applyBubbleZOrderRef.current = null;
            chart.destroy();
            onYAxisBreakpointLabelPositionsRef.current?.([]);
            onYAxisTickLabelPositionsRef.current?.([]);
        };
    }, [
        xAxisTitle,
        yAxisTitle,
        height,
        stroke,
        hideYAxisLabels,
        hideYAxisTitle,
        hideYAxisLine,
        activeSegmentBreakpoints,
        activeSegmentId,
        segmentHighlightConfig,
        shouldRenderGrowthPctLabels,
        shouldRenderInfoLabels,
        isLargeDatasetForInteractions,
        shouldUseSvgRenderer,
    ]);

    // Incremental path: keep the chart instance alive and update only data/scales.
    useEffect(() => {
        if (!window) return;
        const chart = chartRef.current;
        if (!chart) return;

        try {
            const chartContainerElement = typeof chartContainer === 'string' ? document.getElementById(chartContainer) : null;
            const containerWidthPx = chartContainerElement?.clientWidth;
            const chartWidthPx = Number((chart as { width?: unknown }).width);
            const shouldResizeChart =
                typeof containerWidthPx === 'number' &&
                Number.isFinite(containerWidthPx) &&
                containerWidthPx > 0 &&
                (!Number.isFinite(chartWidthPx) || Math.abs(chartWidthPx - containerWidthPx) > 1);
            if (shouldResizeChart) {
                chart.changeSize(containerWidthPx as number, height);
            }

            chart.changeData(chartData);

            const minX: any = getMinBound(chartData, [X_FIELD]);
            const minY: any = getMinBound(chartData, [Y_FIELD]);
            const maxX: any = getMaxBound(chartData, [X_FIELD]);
            const maxY: any = getMaxBound(chartData, [Y_FIELD]);
            const xTickCount = Math.max(1, xTickLabels?.length ?? 0);
            const hasExplicitXAxisCategories = Boolean(xTickLabels && xTickLabels.length > 0);
            const categoryInset = xTickCount > 1 ? 1 / (2 * xTickCount) : 0;
            const xScaleRangeStart = hasExplicitXAxisCategories
                ? xTickCount === 1
                    ? 0.5
                    : categoryInset
                : Math.min(0.05, 0.5 / xTickCount);
            const xScaleRangeEnd = hasExplicitXAxisCategories
                ? xTickCount === 1
                    ? 0.5
                    : 1 - categoryInset
                : 1 - xScaleRangeStart;
            const { maxDiameterPx, columnWidthPx, columnGapPx } = computePpaBubbleSizing(height);
            const xAxisLabelMaxWidthPx = Math.max(0, columnWidthPx + columnGapPx - AXIS_LABEL_HORIZONTAL_GUTTER_PX);
            const xAxisLabelFont = `${X_AXIS_TICK_LABEL_FONT_SIZE_PX}px ${commonSetting.graphFontFamily}`;
            const resolvedYScaleMin =
                typeof yRange?.xMin === 'number' ? yRange.xMin : chartData?.length === 1 ? scaleMinValue(10, minY) : null;
            const resolvedYScaleMax =
                typeof yRange?.xMax === 'number' ? yRange.xMax : chartData?.length === 1 ? scaleMaxValue(10, maxY) : null;
            const estimatedPlotHeightPx = Math.max(
                1,
                height - PPA_CHART_TOP_APPEND_PADDING_PX - PPA_CHART_X_AXIS_CHROME_HEIGHT_PX,
            );
            const yAxisBottomInsetPx = maxDiameterPx * PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO;
            const yScaleRangeStart = Math.min(0.4, yAxisBottomInsetPx / estimatedPlotHeightPx);

            chart.scale({
                [X_FIELD]: {
                    nice: !xTickLabels || xTickLabels.length === 0,
                    min: typeof xRange?.xMin === 'number' ? xRange.xMin : chartData?.length === 1 ? scaleMinValue(10, minX) : null,
                    max: typeof xRange?.xMax === 'number' ? xRange.xMax : chartData?.length === 1 ? scaleMaxValue(10, maxX) : null,
                    ...(xTickLabels && xTickLabels.length > 0
                        ? {
                              tickInterval: 1,
                          }
                        : {}),
                    range: [xScaleRangeStart, xScaleRangeEnd],
                },
                [Y_FIELD]: {
                    nice: !hasCustomYTicks,
                    min: resolvedYScaleMin,
                    max: resolvedYScaleMax,
                    ...(hasCustomYTicks ? { ticks: yTickValues } : {}),
                    range: [yScaleRangeStart, 1],
                },
                [SIZE_FIELD]: {
                    type: 'linear',
                    min: 0,
                    max: 1,
                },
            });

            chart.axis(X_FIELD, {
                title: {
                    text: xAxisTitle,
                    style: {
                        fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                        fontFamily: commonSetting.graphFontFamily,
                        fill: graphLabelColor,
                        fontWeight: '400',
                    },
                },
                label: {
                    formatter: (val: any) => {
                        if (xTickLabels && xTickLabels.length > 0) {
                            const roundedIndex = Math.round(Number(val));
                            if (!Number.isNaN(roundedIndex) && roundedIndex >= 1 && roundedIndex <= xTickLabels.length) {
                                return truncateAxisLabelToWidth(xTickLabels[roundedIndex - 1], xAxisLabelMaxWidthPx, xAxisLabelFont);
                            }
                        }
                        return formatNumWithKMBAlt(val, CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION);
                    },
                    autoRotate: false,
                    autoHide: false,
                    style: {
                        fontSize: X_AXIS_TICK_LABEL_FONT_SIZE_PX,
                        fontFamily: commonSetting.graphFontFamily,
                        fill: graphLabelColor,
                    },
                },
                grid: {
                    line: {
                        style: {
                            stroke: stroke,
                            lineWidth: 0,
                            lineDash: [0, 2],
                        },
                    },
                },
            });

            chart.axis(Y_FIELD, {
                title: hideYAxisTitle
                    ? false
                    : {
                          text: yAxisTitle,
                          style: {
                              fontSize: graphFontSize,
                              fontFamily: commonSetting.graphFontFamily,
                              fill: graphLabelColor,
                              fontWeight: '500',
                          },
                      },
                label: hideYAxisLabels
                    ? false
                    : {
                          formatter: (val: any) => formatYAxisTickLabel(Number(val), CHART_DECIMAL_PRECISION),
                          style: {
                              fontSize: graphFontSize,
                              fontFamily: commonSetting.graphFontFamily,
                              fill: graphLabelColor,
                          },
                      },
                line: hideYAxisLine
                    ? false
                    : {
                          style: {
                              stroke: '#D9D9D9',
                              lineWidth: 1,
                          },
                      },
                grid: {
                    line: {
                        style: {
                            stroke: stroke,
                            lineWidth: 1,
                            lineDash: [1, 2],
                        },
                    },
                },
            });

            chart.axis(COLOR_FIELD, false);
            chart.axis(SIZE_FIELD, false);
            chart.render(true);
            applyBubbleZOrderRef.current?.();

            const resolvedTickValues = Array.isArray(yTickValues) ? yTickValues : [];
            if (onYAxisTickLabelPositionsRef.current) {
                const xAxisAnchor =
                    typeof xRange?.xMin === 'number' ? xRange.xMin : Number.isFinite(minX) ? Number(minX) : 0;
                const tickPositions = resolvedTickValues
                    .map((yValue) => {
                        const point = chart.getXY({
                            [X_FIELD]: xAxisAnchor,
                            [Y_FIELD]: yValue,
                        });
                        return {
                            yValue,
                            topPx: typeof point?.y === 'number' ? point.y : Number.NaN,
                        };
                    })
                    .filter((position) => Number.isFinite(position.topPx));
                onYAxisTickLabelPositionsRef.current(tickPositions);
            }

            if (activeSegmentBreakpoints && onYAxisBreakpointLabelPositionsRef.current) {
                const xDomainMin = typeof xRange?.xMin === 'number' ? xRange.xMin : minX;
                const xDomainMax = typeof xRange?.xMax === 'number' ? xRange.xMax : maxX;
                const xDomainSpan = Math.max(1, xDomainMax - xDomainMin);
                const xRangeSpan = Math.max(0.01, xScaleRangeEnd - xScaleRangeStart);
                const leftInsetRatio = xScaleRangeStart / xRangeSpan;
                const rightInsetRatio = (1 - xScaleRangeEnd) / xRangeSpan;
                const xStart = xDomainMin - xDomainSpan * leftInsetRatio;
                const labels: Array<{ label: string; y: number; color?: string }> = [
                    {
                        label: formatYAxisTickLabel(activeSegmentBreakpoints.maxY, CHART_DECIMAL_PRECISION),
                        y: activeSegmentBreakpoints.maxY,
                        color: Y_AXIS_MIN_MAX_LABEL_COLOR,
                    },
                    {
                        label: `Avg. ${formatYAxisTickLabel(activeSegmentBreakpoints.avgY, CHART_DECIMAL_PRECISION)}`,
                        y: activeSegmentBreakpoints.avgY,
                    },
                    {
                        label: formatYAxisTickLabel(activeSegmentBreakpoints.minY, CHART_DECIMAL_PRECISION),
                        y: activeSegmentBreakpoints.minY,
                        color: Y_AXIS_MIN_MAX_LABEL_COLOR,
                    },
                ];
                const positions = labels
                    .map(({ label, y, color }) => {
                        const point = chart.getXY({
                            [X_FIELD]: xStart,
                            [Y_FIELD]: y,
                        });
                        return {
                            label,
                            topPx: typeof point?.y === 'number' ? point.y : 0,
                            yValue: y,
                            color,
                        };
                    })
                    .filter((position) => Number.isFinite(position.topPx));
                onYAxisBreakpointLabelPositionsRef.current(
                    resolveYAxisBreakpointLabelCollisions(positions, Y_AXIS_BREAKPOINT_LABEL_MIN_GAP_PX),
                );
            } else {
                onYAxisBreakpointLabelPositionsRef.current?.([]);
            }
        } catch (e) {
            console.error('Bubble chart data update error', e);
            onYAxisBreakpointLabelPositionsRef.current?.([]);
            onYAxisTickLabelPositionsRef.current?.([]);
        }
    }, [
        chartData,
        xTickLabels,
        xAxisTitle,
        yAxisTitle,
        xRange?.xMin,
        xRange?.xMax,
        yRange?.xMin,
        yRange?.xMax,
        yTickValues,
        height,
        stroke,
        graphFontSize,
        graphLabelColor,
        hasCustomYTicks,
        estimatedScrollableWidthPx,
        hideYAxisLabels,
        hideYAxisTitle,
        hideYAxisLine,
        activeSegmentBreakpoints,
        commonSetting.graphFontFamily,
        shouldUseSvgRenderer,
    ]);

    return (
        <div>
            {loading ? (
                <div className="flex h-96 w-full items-center justify-center">
                    <Spin />
                </div>
            ) : (
                <div id={chartContainer} />
            )}
        </div>
    );
};
export default memo(BubbleScatterChart);
